import React from 'react';
import { Inbox } from 'lucide-react';
import type { ItemRankingMotivo } from '../../../../src/lib/financeiro/metricasFaturamento';

interface Props {
  ranking: ItemRankingMotivo[];
  /** Atrasos que ficaram sem motivo — emissões anteriores à regra, por exemplo. */
  semMotivo: number;
}

/**
 * Barra horizontal por motivo, no mesmo desenho do AgingTable do Painel Executivo
 * (a outra tabela "quanto de cada faixa" do Financeiro), para as duas telas se
 * lerem do mesmo jeito.
 */
const RankingMotivos: React.FC<Props> = ({ ranking, semMotivo }) => {
  const maior = ranking[0]?.qtde ?? 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Ranking de motivos</h3>
        {semMotivo > 0 && (
          <span className="text-[11px] text-gray-400 dark:text-gray-500">
            {semMotivo} {semMotivo === 1 ? 'atraso sem motivo' : 'atrasos sem motivo'}
          </span>
        )}
      </div>

      {ranking.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
          <Inbox size={22} className="text-gray-300 dark:text-gray-600" />
          <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Nenhum motivo registrado</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 max-w-xs">
            Ou nenhum boleto saiu fora do prazo neste período, ou os que saíram são
            anteriores ao início do registro de motivo.
          </p>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {ranking.map((item) => (
            <li key={item.motivo}>
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate" title={item.motivo}>
                  {item.motivo}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0 tabular-nums">
                  {item.qtde} · {(item.pct * 100).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-300"
                  // A barra é proporcional ao MAIOR motivo, não ao total: com uma
                  // causa dominante as demais viram fios invisíveis se dividirmos
                  // pelo total.
                  style={{ width: maior > 0 ? `${Math.max((item.qtde / maior) * 100, 4)}%` : '0%' }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RankingMotivos;
